import React, { useCallback, useEffect, useId, useRef } from "react";

/**
 * The one overlay in the application: a centred dialog, or a drawer anchored to
 * the right edge.
 *
 * Both variants exist here rather than as two components because everything
 * difficult about an overlay — returning focus to where it came from, keeping
 * Tab inside it, stopping the page behind from scrolling, telling a screen
 * reader what it is — is identical either way, and the moment there are two
 * implementations only one of them gets fixed. There were three before this:
 * this file, EventDetailModal, and a third defined inline in ActivityPage, all
 * with the same chrome copied out and the same four gaps.
 *
 * Modal for content you read and dismiss. Drawer for content you read *against*
 * the table behind it — a drawer leaves the rows visible and gets the full
 * height of the viewport, which suits a long list better than a centred panel
 * capped at 90vh.
 */

export type OverlayVariant = "modal" | "drawer";

/** How wide the centred variant is allowed to get. */
export type OverlayWidth = "md" | "lg" | "xl";

const MODAL_WIDTH: Record<OverlayWidth, string> = {
  md: "max-w-2xl",
  lg: "max-w-3xl",
  xl: "max-w-5xl",
};

interface DetailModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Centred dialog (default) or right-hand drawer. */
  variant?: OverlayVariant;
  /** Width of the centred variant. Ignored by the drawer. */
  width?: OverlayWidth;
  /**
   * Replaces the default "Close" button. A confirmation needs its actions in
   * the footer rather than a dismissal that reads as if nothing happened.
   */
  footer?: React.ReactNode;
  /** Extra chrome for the header, e.g. status badges. */
  headerExtra?: React.ReactNode;
}

/**
 * Elements Tab can reach, in document order, excluding anything disabled or
 * deliberately removed from the tab ring.
 */
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function DetailModal({
  title,
  subtitle,
  onClose,
  children,
  variant = "modal",
  width = "xl",
  footer,
  headerExtra,
}: DetailModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Where focus was before this opened. Restored on the way out: without it
  // focus falls back to <body>, so the next Tab starts from the top of the
  // page rather than the control the reader was just on, and a keyboard user
  // has to walk the whole page again to get back.
  const returnFocusTo = useRef<Element | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;

      // Tab is kept inside the panel. Otherwise it walks out into the page
      // behind, which is still fully tabbable while covered by the backdrop —
      // focus goes somewhere the reader cannot see and the overlay appears to
      // have swallowed the keyboard.
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    returnFocusTo.current = document.activeElement;

    // Focus moves into the panel rather than staying on the trigger behind the
    // backdrop, so Escape and Tab act on the overlay and a screen reader
    // announces the dialog instead of continuing to read the page under it.
    panelRef.current?.focus();

    // The page behind is locked. Scrolling it while an overlay is open moves
    // the content the reader is about to go back to, and on a long table the
    // row they opened is somewhere else when they close it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
      const target = returnFocusTo.current;
      if (target instanceof HTMLElement && document.body.contains(target)) {
        target.focus();
      }
    };
  }, []);

  const isDrawer = variant === "drawer";

  return (
    <div
      className={
        isDrawer
          ? "fixed inset-0 z-50 flex justify-end bg-black/50"
          : "fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      }
      onClick={onClose}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        // role and the accessible name belong on the panel. They used to sit on
        // the backdrop, which made the dialog boundary the whole viewport and
        // put its name on an element containing the page behind it.
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={
          isDrawer
            ? "flex h-full w-full max-w-xl flex-col bg-surface-raised shadow-2xl outline-none"
            : `flex max-h-[90vh] w-full ${MODAL_WIDTH[width]} mx-4 flex-col rounded-2xl bg-surface-raised shadow-2xl outline-none`
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-edge px-6 py-4">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="break-words text-lg font-semibold text-content">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-0.5 break-words text-sm text-content-muted">{subtitle}</p>
            )}
            {headerExtra}
          </div>
          <button
            onClick={onClose}
            className="ml-4 flex-shrink-0 text-content-subtle hover:text-content"
            aria-label="Close"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-edge px-6 py-4">
          {footer ?? (
            <button
              onClick={onClose}
              className="rounded-lg bg-surface-sunken px-5 py-2 text-sm font-medium text-content transition-colors hover:bg-surface-hover"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
